import React, { useState } from 'react';
import { AccordionCard } from '../../Library/AccordionCard';
import { Box, Typography } from '@mui/material';
import { colors } from '../../../utils/theme';
import { HormoneSnapshot, ResultType } from '../../../types';
import { StressData } from '../../../views/StressInsights';
import { CustomIcon } from '../../../atoms';
import { format } from 'date-fns';

export const StressSymptoms = ({
  stressDataMap,
  snapShot,
}: {
  stressDataMap: Record<ResultType, StressData>;
  snapShot: HormoneSnapshot;
}): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const blackFillIcons = [
    'skinCell',
    'bed',
    'saltShaker',
    'fastFood',
    'iceCream02',
  ];

  // Split 'sample_date' (format "YYYY-MM-DD") into parts
  const dateParts = snapShot?.sample_date
    .split('-')
    // Convert each date segment (yr, mo, fy) from string to int
    .map((part) => parseInt(part, 10));

  // Create a Date object. Note: JS uses 0-based months, so adjust month
  const date =
    dateParts && new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);

  // Format the date as 'MMM D, YYYY' (e.g. "Oct 28, 2022")
  const formattedMostRecentDate = format(date, 'MMM D, YYYY');
  return (
    <>
      <AccordionCard
        panelId="stress-symptoms"
        summary={
          <Box display="flex" flexDirection="column" sx={{ width: '100%' }}>
            {formattedMostRecentDate && (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="light" fontSize={12}>
                  LAST UPDATED
                </Typography>
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 50,
                    marginRight: 8,
                    marginLeft: 8,
                    background: colors.greys.BRAND_BLACK,
                  }}
                />
                <Typography variant="mono" fontSize={12}>
                  {formattedMostRecentDate.toUpperCase()}
                </Typography>
              </Box>
            )}

            <Box display="flex" flexDirection="column">
              <Typography
                variant="medium"
                fontSize={20}
                sx={{ mt: 1, mb: 0.5 }}
              >
                Stress symptoms
              </Typography>

              <Typography variant="light" fontSize={14}>
                You may be feeling some of the following:
              </Typography>
            </Box>
            {!isExpanded && (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                sx={{ mt: 2.5 }}
              >
                {stressDataMap[snapShot?.result as ResultType]?.symptoms
                  ?.slice(0, 4)
                  .map((symptom, index) => (
                    <CustomIcon
                      key={`${symptom.icon}-${index}`}
                      name={symptom.icon || 'thumbsUp'}
                      hex="black"
                      sx={{
                        width: 24,
                        height: 24,
                        fill: blackFillIcons.includes(symptom.icon)
                          ? colors.greys.BRAND_BLACK
                          : colors.greys.BASE_WHITE,
                      }}
                    />
                  ))}
              </Box>
            )}
          </Box>
        }
        details={
          <Box display="flex" flexDirection="column" sx={{ mt: 2 }}>
            {stressDataMap[snapShot?.result as ResultType]?.symptoms?.map(
              (symptom, index) => (
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="flex-start"
                  sx={{ mt: index === 0 ? 0 : 2.5 }}
                  key={`{symptom.text}-${index}`}
                >
                  <CustomIcon
                    name={symptom.icon || 'thumbsUp'}
                    hex="black"
                    sx={{
                      width: 24,
                      height: 24,
                      fill: blackFillIcons.includes(symptom.icon)
                        ? colors.greys.BRAND_BLACK
                        : colors.greys.BASE_WHITE,
                      mr: 2,
                      ml: 0.5,
                    }}
                  />
                  <Box display="flex" flexDirection="column">
                    <Typography variant="light" fontSize={16}>
                      {symptom.text}
                    </Typography>
                  </Box>
                </Box>
              )
            )}
          </Box>
        }
        onChange={() => setIsExpanded((prev) => !prev)}
      />
    </>
  );
};
